import { Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { format } from 'date-fns';
import type { CurvePoint } from '../lib/types';
import { describeCurve } from '../lib/curve';

interface Props {
  points: CurvePoint[];
}

export function CurveViz({ points }: Props) {
  if (points.length === 0) {
    return (
      <div className="rounded-3xl border border-[var(--border)] bg-[var(--bg-elev)] p-10 text-center text-[var(--text-muted)]">
        Your ambiguity tolerance curve will appear here after your first saved rehearsal.<br />There is no “good” shape. It is simply a record of what you have practiced.
      </div>
    );
  }

  const data = points.map((p, idx) => ({
    idx,
    date: p.created_at,
    tolerance_after: p.tolerance_after,
    tolerance_before: p.tolerance_before,
    success: p.reappraisal_success,
    reduction: p.residue_reduction,
    theme: p.rumination_theme,
    outcome: p.outcome_type,
  }));

  const desc = describeCurve(points);

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-sm text-[var(--text-muted)]">Ambiguity tolerance curve</div>
          <div className="text-2xl font-medium tracking-tight">How the charge moves after rehearsal</div>
        </div>
        <div className="text-xs text-[var(--text-subtle)] max-w-[260px] text-right">{desc}</div>
      </div>

      <div className="rounded-3xl border border-[var(--border)] bg-[var(--bg-elev)] p-4 sm:p-6">
        <div className="h-[320px] w-full">
          <ResponsiveContainer>
            <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="tol" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="var(--accent)" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="var(--accent)" stopOpacity={0.03} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="2 2" stroke="var(--border)" />
              <XAxis
                dataKey="date"
                tickFormatter={(v) => format(new Date(v), 'MMM d')}
                stroke="var(--text-subtle)"
                tickLine={false}
              />
              <YAxis domain={[0, 10]} stroke="var(--text-subtle)" tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 12, fontSize: 12 }}
                labelFormatter={(v) => format(new Date(v as number), 'PPpp')}
                formatter={(value, name) => {
                  if (name === 'tolerance_after') return [value, 'Tolerance after'];
                  if (name === 'tolerance_before') return [value, 'Before'];
                  if (name === 'success') return [value, 'Reappraisal success'];
                  return [value, name];
                }}
              />
              <Area type="natural" dataKey="tolerance_after" stroke="var(--accent)" strokeWidth={2.5} fill="url(#tol)" />
              <Line type="natural" dataKey="tolerance_before" stroke="var(--text-subtle)" strokeWidth={1.5} strokeDasharray="3 2" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 text-xs text-[var(--text-subtle)] px-2">
          Solid line = tolerance after the rehearsal. Dashed = before. The area under the solid line is not a score — it is simply the trace of what you brought into these private sittings.
        </div>
      </div>

      {points.length > 1 && (
        <div className="text-xs text-[var(--text-muted)] px-1">
          Tap any future rehearsal in History to see its exact contribution to this shape.
        </div>
      )}
    </div>
  );
}
